/**
 * Cat-count milestone definitions. Single source of truth so the modal
 * trigger logic and the visual styling stay in sync.
 *
 * Each tier returns the data the modal needs to render:
 *   - id (used for the localStorage flag — survives logout-per-tier)
 *   - overline label
 *   - title prefix / suffix (interpolated with the cat's name)
 *   - subtitle copy
 *   - badge color theme
 */

export const MILESTONES = [
  {
    count: 1,
    id: "firstCatMilestone:v1",
    overline: "İlk Kilometre Taşı",
    overlineEn: "First Milestone",
    titleTr: (name) => ({ prefix: "İlk kedin: ", name, suffix: "!" }),
    titleEn: (name) => ({ prefix: "Your first cat: ", name, suffix: "!" }),
    subtitleTr: (name) =>
      `StrayPaw ailesine hoş geldin. ${name} artık veritabanında ve mahallenle paylaşıldı.`,
    subtitleEn: (name) =>
      `Welcome to the StrayPaw family. ${name} is now in the database and shared with your neighbourhood.`,
    theme: "amber",
    rewardTr: null,
    rewardEn: null,
  },
  {
    count: 5,
    id: "milestone5Cats:v1",
    overline: "Mahalle Gözcüsü",
    overlineEn: "Neighbourhood Spotter",
    titleTr: (name) => ({ prefix: "", name: "5 kedi!", suffix: "" }),
    titleEn: (name) => ({ prefix: "", name: "5 cats!", suffix: "" }),
    subtitleTr: () =>
      "Mahalleninin gözüsün artık. Beş kediyi tek başına kaydetmek ciddi bir adanmışlık.",
    subtitleEn: () =>
      "You're a neighbourhood watchpoint now. Five cats logged solo is real dedication.",
    theme: "orange",
    rewardTr: "Mahalle Gözcüsü rozeti kazandın",
    rewardEn: "Earned: Neighbourhood Spotter badge",
  },
  {
    count: 10,
    id: "milestone10Cats:v1",
    overline: "Sokak Şampiyonu",
    overlineEn: "Street Champion",
    titleTr: () => ({ prefix: "", name: "10 kedi!", suffix: "" }),
    titleEn: () => ({ prefix: "", name: "10 cats!", suffix: "" }),
    subtitleTr: () =>
      "Çift haneli rakamlar. On kedi seninle güvende — sokak senin sahanda.",
    subtitleEn: () =>
      "Double digits. Ten cats safer because of you — the street is your domain.",
    theme: "rose",
    rewardTr: "Sokak Şampiyonu rozeti + 50 paw puanı",
    rewardEn: "Earned: Street Champion badge + 50 paw points",
  },
  {
    count: 25,
    id: "milestone25Cats:v1",
    overline: "Sokak Kahramanı",
    overlineEn: "Street Hero",
    titleTr: () => ({ prefix: "", name: "25 kedi!", suffix: "" }),
    titleEn: () => ({ prefix: "", name: "25 cats!", suffix: "" }),
    subtitleTr: () =>
      "Bir mahalleyi tek başına dönüştürdün. 25 kedi senin sayende veride. Efsanesin.",
    subtitleEn: () =>
      "You've transformed a neighbourhood single-handed. 25 cats logged. You're a legend.",
    theme: "purple",
    rewardTr: "Sokak Kahramanı rozeti + Premium 7 gün hediye",
    rewardEn: "Earned: Street Hero badge + 7 days Premium gift",
  },
];

export function findMilestone(count) {
  return MILESTONES.find((m) => m.count === count) || null;
}

/**
 * Given the user's current cat count, return progress info for the next
 * milestone (or null if all tiers are unlocked).
 *
 * Shape:
 *   {
 *     next:       MILESTONE,     // the next tier ahead
 *     prev:       MILESTONE | { count: 0 },  // the last tier passed
 *     current:    number,        // user's current count
 *     remaining:  number,        // cats left until `next`
 *     percent:    number,        // 0–100, progress within the current segment
 *     allDone:    boolean,       // true if user has surpassed the top tier
 *   }
 */
export function getMilestoneProgress(count) {
  const c = Math.max(0, Number(count) || 0);
  const next = MILESTONES.find((m) => m.count > c) || null;
  if (!next) {
    return { next: null, prev: MILESTONES[MILESTONES.length - 1], current: c, remaining: 0, percent: 100, allDone: true };
  }
  // The "previous" tier is the last milestone the user has already passed (or 0 if none).
  const prevTier = [...MILESTONES].reverse().find((m) => m.count <= c) || { count: 0 };
  const segmentSpan = next.count - prevTier.count;
  const segmentProgress = c - prevTier.count;
  const percent = segmentSpan > 0 ? Math.min(100, Math.max(0, Math.round((segmentProgress / segmentSpan) * 100))) : 0;
  return {
    next,
    prev: prevTier,
    current: c,
    remaining: Math.max(0, next.count - c),
    percent,
    allDone: false,
  };
}
